import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Gauge, Weight, FileCheck2, ArrowRight, ScanLine, Plus, Check } from "lucide-react";
import api, { formatRON, formatEUR, mediaUrl } from "@/lib/api";
import { useOffer } from "@/context/OfferContext";
import { useTranslation } from "react-i18next";

const PLACEHOLDER = "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?crop=entropy&cs=srgb&fm=jpg&q=80&w=940";

function DealCard({ p, t }) {
  const s = p.specs || {};
  const { has, toggle } = useOffer();
  const inOffer = has(p.id);
  return (
    <div className="group relative bg-white border border-slate-200 flex flex-col hover:border-emerald-500 transition-colors" data-testid={`verified-deal-${p.id}`}>
      <Link to={`/produse/${p.id}`} className="block relative overflow-hidden bg-slate-100 aspect-[4/3]">
        <img src={mediaUrl(p.image) || PLACEHOLDER} alt={p.name} loading="lazy"
          onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <span className="absolute top-0 left-0 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 flex items-center gap-1" data-testid="verified-badge">
          <ShieldCheck className="w-3 h-3" /> {t("deals.verified")}
        </span>
      </Link>
      <div className="p-5 flex flex-col flex-1">
        {p.brand && <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-orange-600 mb-1">{p.brand}</span>}
        <Link to={`/produse/${p.id}`}>
          <h3 className="font-bold text-slate-900 leading-snug mb-3 line-clamp-2 hover:text-orange-600 transition-colors">{p.name}</h3>
        </Link>
        <div className="flex flex-wrap gap-2 mb-4">
          {s.motor_power_kw != null && (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-mono font-semibold text-slate-600 bg-slate-100 px-2 py-1">
              <Gauge className="w-3 h-3 text-slate-400" /> {s.motor_power_kw} kW
            </span>
          )}
          {s.weight_kg != null && (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-mono font-semibold text-slate-600 bg-slate-100 px-2 py-1">
              <Weight className="w-3 h-3 text-slate-400" /> {s.weight_kg} kg
            </span>
          )}
          {s.year != null && (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-mono font-semibold text-slate-600 bg-slate-100 px-2 py-1">{s.year}</span>
          )}
        </div>
        <div className="mb-3 inline-flex items-center gap-1.5 self-start bg-emerald-50 border border-emerald-200 text-emerald-800 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1" data-testid="tender-ready-badge">
          <FileCheck2 className="w-3 h-3" /> {t("deals.tenderBadge")}
        </div>
        <button onClick={() => toggle(p)}
          className={`mb-3 inline-flex items-center justify-center gap-1.5 w-full px-3 py-2 text-[11px] font-bold uppercase tracking-wide border transition-colors ${inOffer ? "bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700" : "bg-white border-slate-300 text-slate-700 hover:border-orange-500 hover:text-orange-600"}`}
          data-testid={`offer-add-${p.id}`}>
          {inOffer ? <><Check className="w-3.5 h-3.5" /> {t("offer.added")}</> : <><Plus className="w-3.5 h-3.5" /> {t("offer.add")}</>}
        </button>        <div className="mt-auto flex items-end justify-between gap-2 pt-3 border-t border-slate-100">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">{t("deals.priceLabel")}</div>
            <div className="text-xl font-black text-slate-900 tracking-tight">{formatRON(p.price)}</div>
            {p.price_eur ? <div className="text-xs text-slate-400 font-mono">≈ {formatEUR(p.price_eur)}</div> : null}
          </div>
          <Link to={`/produse/${p.id}`} className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-emerald-700 text-white px-4 py-2.5 text-xs font-bold uppercase tracking-wide transition-colors" data-testid={`deal-details-${p.id}`}>
            {t("deals.details")} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function VerifiedDeals() {
  const [deals, setDeals] = useState([]);
  const { t } = useTranslation();
  useEffect(() => {
    api.get("/products/verified-deals?limit=8").then((r) => setDeals(r.data)).catch(() => {});
  }, []);

  if (deals.length === 0) return null;

  return (
    <section className="bg-slate-950 text-white relative overflow-hidden" data-testid="verified-deals-section">
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
      <div className="relative max-w-7xl mx-auto px-6 py-20">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-emerald-400" data-testid="deals-eyebrow">
              <ScanLine className="w-4 h-4" /> {t("deals.eyebrow")}
            </span>
            <h2 className="text-3xl lg:text-4xl font-black tracking-tight mt-3" data-testid="deals-title">{t("deals.title")}</h2>
            <p className="text-slate-400 mt-4 leading-relaxed">{t("deals.subtitle")}</p>
          </div>
          <Link to="/produse?verified=1" className="inline-flex items-center gap-2 self-start lg:self-auto border border-slate-700 hover:border-emerald-500 hover:text-emerald-400 px-6 py-3.5 font-bold uppercase tracking-wide text-sm transition-colors shrink-0" data-testid="deals-see-all">
            {t("deals.cta")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {deals.map((p) => <DealCard key={p.id} p={p} t={t} />)}
        </div>
      </div>
    </section>
  );
}
